import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { 
  CheckCircle, Send, Loader2, Star, ThumbsUp, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SatisfactionPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logEvent } = useAuth();
  
  const parcoursId = searchParams.get('parcours') || '';
  
  const [formData, setFormData] = useState({
    conforme_attentes: '',
    objectifs_atteints: 3,
    supports_clairs: 3,
    applicable: '',
    ameliorations: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.conforme_attentes || !formData.applicable) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    
    try {
      await axios.post(`${API}/feedback/satisfaction`, {
        user_id: user.id,
        parcours_id: parcoursId,
        ...formData
      });
      
      logEvent('satisfaction_sent', parcoursId);
      toast.success('Merci pour votre retour !');
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting satisfaction:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f2f2f2]" data-testid="satisfaction-success">
        <Navbar />
        <div className="container mx-auto px-4 md:px-8 py-12 max-w-2xl">
          <Card className="text-center">
            <CardContent className="py-12">
              <div className="h-20 w-20 rounded-full bg-green-100 mx-auto mb-6 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#571925] mb-2 font-['Outfit']">
                Merci pour votre retour !
              </h2>
              <p className="text-gray-600 mb-8">
                Votre avis nous aide à améliorer nos formations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/vipplus')}
                  className="bg-[#ff9a52] hover:bg-[#e08540] text-white rounded-full"
                >
                  Retour à l'accueil
                </Button>
                {parcoursId && (
                  <Button 
                    onClick={() => navigate(`/parcours/${parcoursId}`)}
                    variant="outline"
                    className="border-[#571925] text-[#571925] hover:bg-[#571925] hover:text-white rounded-full"
                  >
                    Revoir le parcours
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f2f2f2]" data-testid="satisfaction-page">
      <Navbar />
      
      <div className="container mx-auto px-4 md:px-8 py-8 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-[#571925] font-['Outfit'] flex items-center gap-2">
              <ThumbsUp className="h-6 w-6" />
              Questionnaire de satisfaction
            </CardTitle>
            <CardDescription>
              Conforme aux exigences Qualiopi - Votre avis nous est précieux
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Question 1 - Conformité */}
              <div className="space-y-4">
                <Label className="text-base font-semibold text-[#1a1a1a]">
                  1. Le contenu de la formation était-il conforme à vos attentes ? *
                </Label>
                <RadioGroup 
                  value={formData.conforme_attentes}
                  onValueChange={(value) => setFormData({...formData, conforme_attentes: value})}
                  className="grid grid-cols-2 gap-4"
                >
                  {['Oui', 'Plutôt oui', 'Plutôt non', 'Non'].map((option) => (
                    <div 
                      key={option}
                      className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.conforme_attentes === option 
                          ? 'border-[#ff9a52] bg-[#ff9a52]/10' 
                          : 'border-gray-200 hover:border-[#cc5759]'
                      }`}
                      onClick={() => setFormData({...formData, conforme_attentes: option})}
                    >
                      <RadioGroupItem value={option} id={`conforme-${option}`} />
                      <Label htmlFor={`conforme-${option}`} className="cursor-pointer">{option}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Question 2 - Objectifs */}
              <div className="space-y-4">
                <Label className="text-base font-semibold text-[#1a1a1a]">
                  2. Les objectifs pédagogiques vous semblent-ils atteints ? *
                </Label>
                <div className="px-2">
                  <div className="flex justify-between text-sm text-gray-500 mb-2">
                    <span>Pas du tout</span>
                    <span>Totalement</span>
                  </div>
                  <Slider
                    value={[formData.objectifs_atteints]}
                    onValueChange={([value]) => setFormData({...formData, objectifs_atteints: value})}
                    min={1}
                    max={5}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between mt-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setFormData({...formData, objectifs_atteints: n})}
                        className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                          formData.objectifs_atteints === n 
                            ? 'bg-[#ff9a52] text-white' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Question 3 - Supports */}
              <div className="space-y-4">
                <Label className="text-base font-semibold text-[#1a1a1a]">
                  3. Les supports (vidéos, exemples) étaient-ils clairs et compréhensibles ? *
                </Label>
                <div className="px-2">
                  <div className="flex justify-between text-sm text-gray-500 mb-2">
                    <span>Pas du tout</span>
                    <span>Totalement</span>
                  </div>
                  <div className="flex justify-between mt-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setFormData({...formData, supports_clairs: n})}
                        className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${
                          formData.supports_clairs >= n 
                            ? 'text-[#ff9a52]' 
                            : 'text-gray-300'
                        }`}
                      >
                        <Star className={`h-6 w-6 ${formData.supports_clairs >= n ? 'fill-current' : ''}`} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Question 4 - Applicable */}
              <div className="space-y-4">
                <Label className="text-base font-semibold text-[#1a1a1a]">
                  4. Pensez-vous pouvoir appliquer ces apprentissages dans votre activité professionnelle ? *
                </Label>
                <RadioGroup 
                  value={formData.applicable}
                  onValueChange={(value) => setFormData({...formData, applicable: value})}
                  className="grid grid-cols-3 gap-4"
                >
                  {['Oui', 'Partiellement', 'Non'].map((option) => (
                    <div 
                      key={option}
                      className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.applicable === option 
                          ? 'border-[#ff9a52] bg-[#ff9a52]/10' 
                          : 'border-gray-200 hover:border-[#cc5759]'
                      }`}
                      onClick={() => setFormData({...formData, applicable: option})}
                    >
                      <RadioGroupItem value={option} id={`applicable-${option}`} />
                      <Label htmlFor={`applicable-${option}`} className="cursor-pointer">{option}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Question 5 - Améliorations */}
              <div className="space-y-4">
                <Label className="text-base font-semibold text-[#1a1a1a] flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  5. Quels points pourraient être améliorés ?
                </Label>
                <Textarea
                  value={formData.ameliorations}
                  onChange={(e) => setFormData({...formData, ameliorations: e.target.value})}
                  placeholder="Vos suggestions nous aident à améliorer nos formations..."
                  className="min-h-[120px] resize-none"
                  data-testid="ameliorations-input"
                />
              </div>

              {/* Submit */}
              <Button 
                type="submit"
                disabled={submitting}
                className="w-full bg-[#ff9a52] hover:bg-[#e08540] text-white rounded-full h-12 font-semibold"
                data-testid="submit-satisfaction-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Envoyer mon avis
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
